// src/components/Product/ProductCard/ProductCard.js 
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardActionArea, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import { supabase } from '../../../services/supabaseClient';
import useImageCycling from '../../../hooks/useImageCycling';
import ProductImage from './ProductImage';
import DiscountBadge from './DiscountBadge';
import ProductInfo from './ProductInfo';
import AddToCartButton from './AddToCartButton';
import FavoriteButton from './FavoriteButton';
import ProductSelectionDialog from './ProductSelectionDialog';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const images = product.images?.length ? product.images : ['/placeholder-image.png'];
  
  // Use the image cycling hook
  const {
    currentImageIndex,
    isHovered,
    nextImage,
    prevImage,
    handleMouseEnter,
    handleMouseLeave
  } = useImageCycling(images, 900);
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Product variants state
  const [productColors, setProductColors] = useState([]);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity] = useState(1);
  
  // Discount state - now simplified since prices are in products table
  const [discountInfo, setDiscountInfo] = useState({
    hasDiscount: false,
    discountText: '',
    price: 0,
    newPrice: 0
  });

  // Calculate discount information - simplified for product-level pricing
  const calculateDiscountInfo = useCallback(() => {
    try {
      const price = product.price || 0;
      const newPrice = product.newPrice || product.new_price || null;
      
      if (newPrice && newPrice < price) {
        const discountPercent = Math.round(((price - newPrice) / price) * 100);
        setDiscountInfo({
          hasDiscount: true,
          discountText: `${discountPercent}%`,
          price: price,
          newPrice: newPrice
        });
      } else {
        setDiscountInfo({
          hasDiscount: false,
          discountText: '',
          price: price,
          newPrice: price
        });
      }
    } catch (err) {
      console.error('Error calculating discount info:', err);
      setDiscountInfo({
        hasDiscount: false,
        discountText: '',
        price: product.price || 0,
        newPrice: product.price || 0
      });
    }
  }, [product.price, product.newPrice, product.new_price]);

  // Calculate discount on component mount and when product changes
  useEffect(() => {
    calculateDiscountInfo();
  }, [calculateDiscountInfo]);

  // Dialog handlers
  const handleOpenDialog = async (e) => {
    e.preventDefault();
    setOpen(true);
    setLoading(true);
    setError('');
    
    try {
      // Updated query to include new fields and order by color_order
      const { data: colorsData, error: colorsError } = await supabase
        .from('product_colors')
        .select(`
          *,
          colors(name),
          product_color_images(image_url, image_order)
        `)
        .eq('product_id', product.id)
        .order('color_order');
        
      if (colorsError) throw colorsError;
      
      // Transform colors data to include ordered images
      const transformedColors = colorsData.map(color => ({
        ...color,
        images: color.product_color_images
          ? color.product_color_images
              .sort((a, b) => (a.image_order || 0) - (b.image_order || 0))
              .map(img => img.image_url)
          : []
      }));
      
      setProductColors(transformedColors);
      
      if (transformedColors?.length > 0) {
        // Find the home color first, fallback to first color
        const homeColorIndex = transformedColors.findIndex(color => color.is_home_color);
        const initialColorIndex = homeColorIndex >= 0 ? homeColorIndex : 0;
        
        setSelectedColorIndex(initialColorIndex);
        await fetchColorDetails(transformedColors[initialColorIndex].id);
      }
    } catch (err) {
      console.error('Error fetching product colors:', err);
      setError('حدث خطأ أثناء تحميل بيانات المنتج');
    } finally {
      setLoading(false);
    }
  };

  // Fetch size details - now only fetches stock and size info
  const fetchColorDetails = async (colorId) => {
    try {
      const { data: sizesData, error: sizesError } = await supabase
        .from('product_color_sizes')
        .select('size, stock')
        .eq('product_color_id', colorId)
        .order('size');
        
      if (sizesError) throw sizesError;
      
      setAvailableSizes(sizesData);
      setSelectedSize('');
    } catch (err) {
      console.error('Error fetching color details:', err);
      setError('حدث خطأ أثناء تحميل تفاصيل المنتج');
    }
  };

  // Color selection handler
  const handleColorChange = (index) => {
    setSelectedColorIndex(index);
    fetchColorDetails(productColors[index].id);
  };

  // Size selection handler
  const handleSizeChange = (size) => {
    setSelectedSize(size);
  };

  // Add to cart handler - updated to use product-level pricing
  const handleAddToCart = () => {
    if (availableSizes.length > 0 && !selectedSize) {
      setError('الرجاء اختيار المقاس');
      return;
    }

    const sizeData = availableSizes.find(s => s.size === selectedSize);
    
    if (!sizeData) {
      setError('المقاس غير متوفر');
      return;
    }

    if (quantity > sizeData.stock) {
      setError('الكمية المطلوبة تتجاوز المخزون المتاح');
      return;
    }

    const selectedColor = productColors[selectedColorIndex];
    
    const cartItem = {
      id: product.id,
      name_ar: product.name_ar,
      description_ar: product.description_ar,
      colorId: selectedColor.id,
      colorName: selectedColor.colors?.name || selectedColor.color,
      size: selectedSize,
      price: discountInfo.hasDiscount ? discountInfo.newPrice : discountInfo.price, // Use discounted price if available
      quantity: quantity,
      image: selectedColor.images?.[0] || images[0] || '/placeholder-image.png'
    };
    
    addToCart(cartItem);
    handleClose();
  };

  // Close dialog
  const handleClose = () => {
    setOpen(false);
    setSelectedColorIndex(0);
    setSelectedSize('');
    setError('');
  };

  // Check if add to cart should be disabled
  const isAddToCartDisabled = loading || 
    !selectedSize || 
    availableSizes.find(s => s.size === selectedSize)?.stock < 1;

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          height: isMobile ? '300px' : '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease-in-out',
          zIndex: 1,
          '&:hover': { 
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            transform: 'translateY(-4px)',
            zIndex: 2
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Discount Badge */}
        <DiscountBadge 
          discountText={discountInfo.discountText} 
          hasDiscount={discountInfo.hasDiscount}
          isMobile={isMobile} 
        />
        
        {/* Favorite Button - positioned at top-right */}
        <FavoriteButton productId={product.id} isMobile={isMobile} />

        <CardActionArea 
          component={Link} 
          to={`/product/${product.id}`}
          sx={{ flexGrow: 1 }}
        >
          <ProductImage
            images={images}
            currentImageIndex={currentImageIndex}
            isHovered={isHovered}
            nextImage={nextImage}
            prevImage={prevImage}
            productName={product.name_ar}
            isMobile={isMobile}
          />

          <ProductInfo
            product={product}
            isMobile={isMobile}
            discountInfo={discountInfo}
          />
        </CardActionArea>

        <AddToCartButton 
          onAddToCart={handleOpenDialog}
          isMobile={isMobile}
        />
      </Card>

      <ProductSelectionDialog
        open={open}
        onClose={handleClose}
        loading={loading}
        error={error}
        product={product}
        productColors={productColors}
        selectedColorIndex={selectedColorIndex}
        onColorChange={handleColorChange}
        availableSizes={availableSizes}
        selectedSize={selectedSize}
        onSizeChange={handleSizeChange}
        onAddToCart={handleAddToCart}
        isAddToCartDisabled={isAddToCartDisabled}
      />
    </>
  );
};

export default ProductCard;
